export const VISIBILITY_HISTORY_VERSION = 1 as const;

export type VisibilityComposition = "all" | "any";
export type UtcCalendarPeriod = "hour" | "day" | "week" | "month" | "year";

interface VisibilityTriggerBase {
  id?: string;
  enabled?: boolean;
}

export interface LifetimeMaximumTrigger extends VisibilityTriggerBase {
  type: "lifetime-maximum";
  maximum: number;
}

export interface RollingWindowTrigger extends VisibilityTriggerBase {
  type: "rolling-window";
  maximum: number;
  durationSeconds: number;
}

export interface CooldownTrigger extends VisibilityTriggerBase {
  type: "cooldown";
  cooldownSeconds: number;
  lifetimeMaximum?: number;
}

export interface UtcCalendarWindowTrigger extends VisibilityTriggerBase {
  type: "utc-calendar-window";
  maximum: number;
  period: UtcCalendarPeriod;
  weekStartsOn?: number;
}

export type VisibilityTrigger =
  | LifetimeMaximumTrigger
  | RollingWindowTrigger
  | CooldownTrigger
  | UtcCalendarWindowTrigger;

export interface VisibilityPolicy {
  composition: VisibilityComposition;
  triggers: VisibilityTrigger[];
}

export interface VisibilityHistory {
  version: typeof VISIBILITY_HISTORY_VERSION;
  configFingerprint: string;
  lifetimeViewCount: number;
  viewedAt: string[];
}

export interface VisibilityMountState {
  mountId?: string;
  counted: boolean;
}

export type VisibilityIneligibilityReason =
  | "lifetime-maximum-reached"
  | "rolling-window-maximum-reached"
  | "cooldown-active"
  | "calendar-window-maximum-reached";

export interface VisibilityTriggerEvaluation {
  trigger: VisibilityTrigger;
  eligible: boolean;
  reason?: VisibilityIneligibilityReason;
  currentCount: number;
  maximum?: number;
  windowStart?: string;
  windowEnd?: string;
  nextEligibleAt?: string;
}

export interface VisibilityEligibility {
  eligible: boolean;
  composition: VisibilityComposition;
  evaluatedAt: string;
  nextEligibleAt?: string;
  results: VisibilityTriggerEvaluation[];
}

export interface VisibilityMountEvaluation extends VisibilityEligibility {
  shouldRecordView: boolean;
}

export interface RecordVisibilityResult {
  history: VisibilityHistory;
  mount: VisibilityMountState;
  recorded: boolean;
}

export interface VisibilityTriggerDefinition {
  type: VisibilityTrigger["type"];
  label: string;
  description: string;
  fields: readonly string[];
}

export const VISIBILITY_COMPOSITION_OPTIONS = [
  {
    value: "all",
    label: "All conditions",
    description: "Show only when every selected condition allows another view.",
  },
  {
    value: "any",
    label: "Any condition",
    description: "Show when at least one selected condition allows another view.",
  },
] as const satisfies readonly {
  value: VisibilityComposition;
  label: string;
  description: string;
}[];

export const VISIBILITY_TRIGGER_DEFINITIONS = [
  {
    type: "lifetime-maximum",
    label: "Lifetime maximum",
    description: "Limit the total number of views for a user.",
    fields: ["maximum"],
  },
  {
    type: "rolling-window",
    label: "Window from first view",
    description: "Limit views in a window anchored to the first view in each cycle.",
    fields: ["maximum", "durationSeconds"],
  },
  {
    type: "cooldown",
    label: "Cooldown between views",
    description: "Require time between views, optionally with a lifetime maximum.",
    fields: ["cooldownSeconds", "lifetimeMaximum"],
  },
  {
    type: "utc-calendar-window",
    label: "UTC calendar window",
    description: "Limit views in fixed UTC hour, day, week, month, or year windows.",
    fields: ["maximum", "period", "weekStartsOn"],
  },
] as const satisfies readonly VisibilityTriggerDefinition[];

const PERIODS = new Set<UtcCalendarPeriod>(["hour", "day", "week", "month", "year"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function positiveInteger(value: unknown): number | undefined {
  const numberValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) return undefined;
  return Math.max(1, Math.floor(numberValue));
}

function nonNegativeInteger(value: unknown): number | undefined {
  const numberValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numberValue) || numberValue < 0) return undefined;
  return Math.floor(numberValue);
}

function timestamp(value: unknown): number | undefined {
  if (isRecord(value)) {
    return timestamp(value.timestamp ?? value.viewedAt ?? value.at ?? value.date);
  }

  if (value instanceof Date) {
    const result = value.getTime();
    return Number.isFinite(result) ? result : undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const milliseconds = value > 0 && value < 10_000_000_000 ? value * 1000 : value;
    return Number.isFinite(new Date(milliseconds).getTime()) ? milliseconds : undefined;
  }

  if (typeof value !== "string" || !value.trim()) return undefined;
  const result = Date.parse(value);
  return Number.isFinite(result) ? result : undefined;
}

function normaliseTimestamps(values: unknown[]): number[] {
  return values.map(timestamp).filter((value): value is number => value !== undefined)
    .sort((left, right) => left - right);
}

function normaliseTrigger(value: unknown): VisibilityTrigger | undefined {
  if (!isRecord(value) || value.enabled === false) return undefined;

  const rawType = String(value.type ?? value.kind ?? value.trigger ?? "").trim().toLowerCase();
  const typeAliases: Record<string, VisibilityTrigger["type"]> = {
    lifetime: "lifetime-maximum",
    maximum: "lifetime-maximum",
    "lifetime-maximum": "lifetime-maximum",
    rolling: "rolling-window",
    "rolling-window": "rolling-window",
    cooldown: "cooldown",
    calendar: "utc-calendar-window",
    "calendar-window": "utc-calendar-window",
    "utc-calendar-window": "utc-calendar-window",
  };
  const type = typeAliases[rawType];
  const id = typeof value.id === "string" && value.id.trim() ? value.id.trim() : undefined;

  if (type === "lifetime-maximum") {
    const maximum = positiveInteger(value.maximum ?? value.maxViews ?? value.limit);
    return maximum ? { type, maximum, ...(id ? { id } : {}) } : undefined;
  }

  if (type === "rolling-window") {
    const maximum = positiveInteger(value.maximum ?? value.maxViews ?? value.limit);
    const durationSeconds = positiveInteger(
      value.durationSeconds ?? value.windowSeconds ?? value.duration,
    );
    return maximum && durationSeconds
      ? { type, maximum, durationSeconds, ...(id ? { id } : {}) }
      : undefined;
  }

  if (type === "cooldown") {
    const cooldownSeconds = positiveInteger(
      value.cooldownSeconds ?? value.durationSeconds ?? value.cooldown,
    );
    const lifetimeMaximum = positiveInteger(
      value.lifetimeMaximum ?? value.maximum ?? value.maxViews,
    );
    return cooldownSeconds
      ? { type, cooldownSeconds, ...(lifetimeMaximum ? { lifetimeMaximum } : {}), ...(id ? { id } : {}) }
      : undefined;
  }

  if (type === "utc-calendar-window") {
    const maximum = positiveInteger(value.maximum ?? value.maxViews ?? value.limit);
    const rawPeriod = String(value.period ?? value.window ?? "day").toLowerCase() as UtcCalendarPeriod;
    const period = PERIODS.has(rawPeriod) ? rawPeriod : "day";
    const weekStartsOnValue = nonNegativeInteger(value.weekStartsOn);
    const weekStartsOn = weekStartsOnValue !== undefined && weekStartsOnValue <= 6
      ? weekStartsOnValue
      : 1;
    return maximum
      ? { type, maximum, period, weekStartsOn, ...(id ? { id } : {}) }
      : undefined;
  }

  return undefined;
}

export function normalizeVisibilityPolicy(value: unknown): VisibilityPolicy {
  if (!isRecord(value)) return { composition: "all", triggers: [] };

  const rawComposition = String(value.composition ?? value.match ?? value.operator ?? "all").toLowerCase();
  const composition: VisibilityComposition = rawComposition === "any" || rawComposition === "or"
    ? "any"
    : "all";
  const source = Array.isArray(value.triggers)
    ? value.triggers
    : Array.isArray(value.conditions)
      ? value.conditions
      : value.type || value.kind
        ? [value]
        : [];

  return {
    composition,
    triggers: source.map(normaliseTrigger).filter((trigger): trigger is VisibilityTrigger => Boolean(trigger)),
  };
}

function semanticPolicy(policy: VisibilityPolicy): unknown {
  return {
    composition: policy.composition,
    triggers: policy.triggers
      .map(({ id: _id, enabled: _enabled, ...trigger }) => trigger)
      .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
  };
}

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
  if (!isRecord(value)) return JSON.stringify(value);
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(",")}}`;
}

export function createVisibilityConfigFingerprint(value: unknown): string {
  const serialized = stableSerialize(semanticPolicy(normalizeVisibilityPolicy(value)));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `v${VISIBILITY_HISTORY_VERSION}-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function unwrapHistory(value: unknown): unknown {
  if (typeof value === "string") {
    try {
      return unwrapHistory(JSON.parse(value));
    } catch {
      return value;
    }
  }
  if (!isRecord(value)) return value;
  if (isRecord(value.value)) return unwrapHistory(value.value);
  if (Array.isArray(value.value)) return value.value;
  if (typeof value.data === "string") {
    return unwrapHistory(value.data);
  }
  if (isRecord(value.data) && (
    Array.isArray(value.data.viewedAt)
    || Array.isArray(value.data.timestamps)
    || Array.isArray(value.data.views)
    || Array.isArray(value.data.history)
    || value.data.lifetimeViewCount !== undefined
  )) {
    return value.data;
  }
  return value;
}

export function normalizeVisibilityHistory(
  value: unknown,
  configFingerprint = "",
): VisibilityHistory {
  const unwrapped = unwrapHistory(value);
  const record = isRecord(unwrapped) ? unwrapped : {};
  const rawHistory = Array.isArray(unwrapped)
    ? unwrapped
    : Array.isArray(record.viewedAt)
      ? record.viewedAt
      : Array.isArray(record.timestamps)
        ? record.timestamps
        : Array.isArray(record.views)
          ? record.views
          : Array.isArray(record.history)
            ? record.history
            : [];
  const singleTimestamps = [
    record.firstViewedAt,
    record.lastViewedAt,
    record.viewedAt instanceof Array ? undefined : record.viewedAt,
  ];
  const viewedAt = normaliseTimestamps([...rawHistory, ...singleTimestamps]);
  const explicitCount = nonNegativeInteger(
    record.lifetimeViewCount ?? record.lifetimeCount ?? record.viewCount ?? record.count,
  );
  const storedFingerprint = typeof record.configFingerprint === "string"
    ? record.configFingerprint
    : typeof record.fingerprint === "string"
      ? record.fingerprint
      : "";

  return {
    version: VISIBILITY_HISTORY_VERSION,
    configFingerprint: storedFingerprint || configFingerprint,
    lifetimeViewCount: Math.max(explicitCount ?? 0, viewedAt.length),
    viewedAt: viewedAt.map((value) => new Date(value).toISOString()),
  };
}

export function reconcileVisibilityHistory(
  value: unknown,
  policyValue: unknown,
  options: { onFingerprintChange?: "reset" | "preserve"; now?: Date | number | string } = {},
): VisibilityHistory {
  const policy = normalizeVisibilityPolicy(policyValue);
  const fingerprint = createVisibilityConfigFingerprint(policy);
  const history = normalizeVisibilityHistory(value, fingerprint);

  if (
    history.configFingerprint
    && history.configFingerprint !== fingerprint
    && options.onFingerprintChange !== "preserve"
  ) {
    return {
      version: VISIBILITY_HISTORY_VERSION,
      configFingerprint: fingerprint,
      lifetimeViewCount: 0,
      viewedAt: [],
    };
  }

  return pruneVisibilityHistory(
    { ...history, configFingerprint: fingerprint },
    policy,
    options.now,
  );
}

function toNow(value?: Date | number | string): number {
  return timestamp(value ?? Date.now()) ?? Date.now();
}

export function getUtcCalendarWindow(
  period: UtcCalendarPeriod,
  nowValue?: Date | number | string,
  weekStartsOn = 1,
): { start: Date; end: Date } {
  const now = new Date(toNow(nowValue));
  let start: Date;
  let end: Date;

  switch (period) {
    case "hour":
      start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours()));
      end = new Date(start.getTime() + 60 * 60 * 1000);
      break;
    case "day":
      start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      break;
    case "week": {
      const normalizedWeekStart = weekStartsOn >= 0 && weekStartsOn <= 6 ? weekStartsOn : 1;
      const dayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
      const daysSinceStart = (now.getUTCDay() - normalizedWeekStart + 7) % 7;
      start = new Date(dayStart - daysSinceStart * 24 * 60 * 60 * 1000);
      end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
      break;
    }
    case "month":
      start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
      break;
    case "year":
      start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      end = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1));
      break;
  }

  return { start, end };
}

function activeRollingCycle(
  values: number[],
  durationMilliseconds: number,
  now: number,
): { start: number; end: number; values: number[] } | undefined {
  let cycleStart: number | undefined;
  let cycleValues: number[] = [];

  for (const value of values) {
    if (cycleStart === undefined || value >= cycleStart + durationMilliseconds) {
      cycleStart = value;
      cycleValues = [value];
    } else {
      cycleValues.push(value);
    }
  }

  if (cycleStart === undefined || now >= cycleStart + durationMilliseconds) return undefined;
  return { start: cycleStart, end: cycleStart + durationMilliseconds, values: cycleValues };
}

function evaluateTrigger(
  trigger: VisibilityTrigger,
  history: VisibilityHistory,
  values: number[],
  now: number,
): VisibilityTriggerEvaluation {
  if (trigger.type === "lifetime-maximum") {
    const eligible = history.lifetimeViewCount < trigger.maximum;
    return {
      trigger,
      eligible,
      currentCount: history.lifetimeViewCount,
      maximum: trigger.maximum,
      ...(!eligible ? { reason: "lifetime-maximum-reached" as const } : {}),
    };
  }

  if (trigger.type === "rolling-window") {
    const cycle = activeRollingCycle(values, trigger.durationSeconds * 1000, now);
    const currentCount = cycle?.values.length ?? 0;
    const eligible = currentCount < trigger.maximum;
    return {
      trigger,
      eligible,
      currentCount,
      maximum: trigger.maximum,
      ...(cycle ? {
        windowStart: new Date(cycle.start).toISOString(),
        windowEnd: new Date(cycle.end).toISOString(),
      } : {}),
      ...(!eligible && cycle ? {
        reason: "rolling-window-maximum-reached" as const,
        nextEligibleAt: new Date(cycle.end).toISOString(),
      } : {}),
    };
  }

  if (trigger.type === "cooldown") {
    const lifetimeReached = trigger.lifetimeMaximum !== undefined
      && history.lifetimeViewCount >= trigger.lifetimeMaximum;
    const lastView = values.at(-1);
    const cooldownEnd = lastView === undefined ? undefined : lastView + trigger.cooldownSeconds * 1000;
    const cooldownActive = cooldownEnd !== undefined && now < cooldownEnd;
    const eligible = !lifetimeReached && !cooldownActive;
    return {
      trigger,
      eligible,
      currentCount: history.lifetimeViewCount,
      ...(trigger.lifetimeMaximum !== undefined ? { maximum: trigger.lifetimeMaximum } : {}),
      ...(lifetimeReached ? { reason: "lifetime-maximum-reached" as const } : {}),
      ...(!lifetimeReached && cooldownActive ? {
        reason: "cooldown-active" as const,
        nextEligibleAt: new Date(cooldownEnd).toISOString(),
      } : {}),
    };
  }

  const window = getUtcCalendarWindow(trigger.period, now, trigger.weekStartsOn);
  const start = window.start.getTime();
  const end = window.end.getTime();
  const currentCount = values.filter((value) => value >= start && value < end).length;
  const eligible = currentCount < trigger.maximum;
  return {
    trigger,
    eligible,
    currentCount,
    maximum: trigger.maximum,
    windowStart: window.start.toISOString(),
    windowEnd: window.end.toISOString(),
    ...(!eligible ? {
      reason: "calendar-window-maximum-reached" as const,
      nextEligibleAt: window.end.toISOString(),
    } : {}),
  };
}

export function evaluateVisibilityPolicy(
  policyValue: unknown,
  historyValue: unknown,
  nowValue?: Date | number | string,
): VisibilityEligibility {
  const policy = normalizeVisibilityPolicy(policyValue);
  const now = toNow(nowValue);
  const history = reconcileVisibilityHistory(historyValue, policy, { now });
  const values = normaliseTimestamps(history.viewedAt);
  const results = policy.triggers.map((trigger) => evaluateTrigger(trigger, history, values, now));
  const eligible = results.length === 0
    || (policy.composition === "all"
      ? results.every((result) => result.eligible)
      : results.some((result) => result.eligible));
  const blocked = results.filter((result) => !result.eligible);
  const temporary = blocked
    .map((result) => timestamp(result.nextEligibleAt))
    .filter((value): value is number => value !== undefined);
  const hasPermanentBlock = blocked.some((result) => !result.nextEligibleAt);
  const cannotBecomeEligible = policy.composition === "all" && hasPermanentBlock;
  const nextEligibleTimestamp = eligible || cannotBecomeEligible || temporary.length === 0
    ? undefined
    : policy.composition === "all"
      ? Math.max(...temporary)
      : Math.min(...temporary);

  return {
    eligible,
    composition: policy.composition,
    evaluatedAt: new Date(now).toISOString(),
    ...(nextEligibleTimestamp !== undefined
      ? { nextEligibleAt: new Date(nextEligibleTimestamp).toISOString() }
      : {}),
    results,
  };
}

export function pruneVisibilityHistory(
  historyValue: unknown,
  policyValue: unknown,
  nowValue?: Date | number | string,
): VisibilityHistory {
  const policy = normalizeVisibilityPolicy(policyValue);
  const fingerprint = createVisibilityConfigFingerprint(policy);
  const history = normalizeVisibilityHistory(historyValue, fingerprint);
  const now = toNow(nowValue);
  const values = normaliseTimestamps(history.viewedAt).filter((value) => value <= now);
  const retained = new Set<number>();

  for (const trigger of policy.triggers) {
    if (trigger.type === "rolling-window") {
      activeRollingCycle(values, trigger.durationSeconds * 1000, now)
        ?.values.forEach((value) => retained.add(value));
    } else if (trigger.type === "cooldown") {
      const lastView = values.at(-1);
      if (lastView !== undefined && now < lastView + trigger.cooldownSeconds * 1000) {
        retained.add(lastView);
      }
    } else if (trigger.type === "utc-calendar-window") {
      const { start, end } = getUtcCalendarWindow(trigger.period, now, trigger.weekStartsOn);
      values
        .filter((value) => value >= start.getTime() && value < end.getTime())
        .forEach((value) => retained.add(value));
    }
  }

  return {
    ...history,
    configFingerprint: fingerprint,
    viewedAt: [...retained].sort((left, right) => left - right).map((value) => new Date(value).toISOString()),
  };
}

export function createVisibilityMountState(mountId?: string): VisibilityMountState {
  return { ...(mountId ? { mountId } : {}), counted: false };
}

export function evaluateVisibilityForMount(
  policyValue: unknown,
  historyValue: unknown,
  mount: VisibilityMountState,
  nowValue?: Date | number | string,
): VisibilityMountEvaluation {
  const eligibility = evaluateVisibilityPolicy(policyValue, historyValue, nowValue);
  return {
    ...eligibility,
    shouldRecordView: eligibility.eligible && !mount.counted,
  };
}

export function recordVisibilityView(
  policyValue: unknown,
  historyValue: unknown,
  mount: VisibilityMountState,
  nowValue?: Date | number | string,
): RecordVisibilityResult {
  const now = toNow(nowValue);
  const policy = normalizeVisibilityPolicy(policyValue);
  const history = reconcileVisibilityHistory(historyValue, policy, { now });
  const evaluation = evaluateVisibilityForMount(policy, history, mount, now);

  if (!evaluation.shouldRecordView) {
    return { history, mount: { ...mount }, recorded: false };
  }

  const recordedHistory: VisibilityHistory = {
    ...history,
    lifetimeViewCount: history.lifetimeViewCount + 1,
    viewedAt: [...history.viewedAt, new Date(now).toISOString()],
  };

  return {
    history: pruneVisibilityHistory(recordedHistory, policy, now),
    mount: { ...mount, counted: true },
    recorded: true,
  };
}
